import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { DEFAULT_LANG, LANGUAGES, translate } from "./translations";

const STORAGE_KEY = "tarq-lang";
const LangContext = createContext(null);

/** Detect browser language and match to one of our supported codes. */
function detectInitialLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && LANGUAGES.some((l) => l.code === stored)) return stored;
  const nav = (navigator.language || "").slice(0, 2).toLowerCase();
  if (LANGUAGES.some((l) => l.code === nav)) return nav;
  return DEFAULT_LANG;
}

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(DEFAULT_LANG);

  useEffect(() => {
    setLangState(detectInitialLang());
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((code) => {
    if (!LANGUAGES.some((l) => l.code === code)) return;
    setLangState(code);
    try {
      window.localStorage.setItem(STORAGE_KEY, code);
    } catch (e) {
      /* localStorage may be blocked — fail silently */
    }
  }, []);

  const t = useCallback((key) => translate(key, lang), [lang]);

  return (
    <LangContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LangContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LangContext);
  if (!ctx) {
    // Allow components to render without provider (returns t that falls back to PT)
    return { lang: DEFAULT_LANG, setLang: () => {}, t: (k) => translate(k, DEFAULT_LANG) };
  }
  return ctx;
}
